import { FC, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Box, Collapse, HStack, useDisclosure, VStack } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { TeamMembersRequestType } from '__generated__/GQL';
import { formatAmount } from 'common/formatAmount';
import Callout from 'components/_core/Callout';
import * as Form from 'components/_core/form';
import SunshineCard from 'components/_core/SunshineCard';
import SunshineIcon from 'components/_core/SunshineIcon';
import DetailSidebar from 'components/DetailSidebar';
import DatePicker from 'components/LegacyInput/DatePicker';
import {
  isVirtualBudgetCardRequest,
  PaymentCardRequest,
} from 'features/Team/pages/Requests/hooks/useRequests';

import { VirtualPaymentCardLimits } from '../../hooks/useVirtualPaymentCardLimits';
import PaymentCardRequestFormFooter from '../PaymentCardRequestFormFooter';
import Summary from '../Summary';
import DateInput from './components/DateInput';
import locales from './locales';
import { VirtualPaymentCardRequestFormData } from './types';
import {
  formatDeactivationDate,
  formatRelativeDate,
  getRelativeDateTitle,
} from './utils';

const DELAY_IN_DAYS = [1, 7, 14, 30];

const DEACTIVATED_DATE_OPTIONS = DELAY_IN_DAYS.map((days) => ({
  label: locales.formatString(locales.deactivateAt.date, {
    date: formatRelativeDate(days, 'PP'),
  }),
  title: getRelativeDateTitle(days),
  value: formatRelativeDate(days, 'yyyy-MM-dd'),
}));

/**
 * we need to use a custom input value to avoid
 * having the date picker in sync with other options
 */
const CUSTOM_DEACTIVATION_DATE_INPUT_VALUE = 'custom-deactivation-date';

const isCustomDate = (date: string | undefined) =>
  !DEACTIVATED_DATE_OPTIONS.find((option) => option.value === date);

interface VirtualPaymentCardRequestFormProps {
  handleRejectRequest: () => void;
  handleApproveRequest: (formData: VirtualPaymentCardRequestFormData) => void;
  isApprovingRequest: boolean;
  isRejectingRequest: boolean;
  request: PaymentCardRequest;
  limits: VirtualPaymentCardLimits;
}

const LIMIT_PAYMENT_ALL = {
  max: 5000,
  min: 0.01,
};

const VirtualPaymentCardRequestForm: FC<VirtualPaymentCardRequestFormProps> = ({
  handleApproveRequest,
  handleRejectRequest,
  isApprovingRequest,
  isRejectingRequest,
  limits,
  request,
}) => {
  const isVirtualBudgetCard = isVirtualBudgetCardRequest(request);

  const {
    defaultLimitPaymentWeek,
    isOptionNfcEnabled,
    isOptionOnlineEnabled,
    maxLimitPaymentWeek,
  } = limits;

  const formSchema = z.object({
    deactivateAt: z.string().optional().transform(formatDeactivationDate),
    isOptionNfcEnabled: z.boolean(),
    isOptionOnlineEnabled: z.boolean(),
    limitPaymentAll: z
      .number({
        invalid_type_error: locales.required,
        required_error: locales.required,
      })
      .min(
        LIMIT_PAYMENT_ALL.min,
        locales.formatString(locales.limitPaymentAll.errors.min, {
          min: formatAmount({
            currency: 'EUR',
            forcePlusSign: false,
            value: LIMIT_PAYMENT_ALL.min,
          }),
        }),
      )
      .max(
        LIMIT_PAYMENT_ALL.max,
        locales.formatString(locales.limitPaymentAll.errors.max, {
          max: formatAmount({
            currency: 'EUR',
            forcePlusSign: false,
            value: LIMIT_PAYMENT_ALL.max,
            withNoMinorUnitDigits: true,
          }),
        }),
      )
      .optional(),
    limitPaymentWeek: z
      .number({ invalid_type_error: locales.required })
      .min(0, locales.required)
      .max(
        maxLimitPaymentWeek,
        locales.formatString(locales.limitPaymentWeek.error, {
          max: formatAmount({
            currency: 'EUR',
            forcePlusSign: false,
            value: maxLimitPaymentWeek,
            withNoMinorUnitDigits: true,
          }),
        }),
      ),
  });

  const {
    control,
    formState: { errors, isValid },
    getValues,
    handleSubmit,
    register,
    resetField,
    setValue,
    watch,
  } = useForm<z.infer<typeof formSchema>>({
    defaultValues: {
      isOptionNfcEnabled,
      isOptionOnlineEnabled,
      limitPaymentAll: isVirtualBudgetCard
        ? request.virtualBudgetCardOrderRequesterPayload?.limitPaymentAll
        : undefined,
      limitPaymentWeek: isVirtualBudgetCard
        ? request.virtualBudgetCardOrderRequesterPayload?.limitPaymentAll
        : defaultLimitPaymentWeek,
    },
    mode: 'onChange',
    resolver: zodResolver(formSchema),
  });

  const { isOpen: withDeactivationDate, onToggle: toggleDeactivationDate } =
    useDisclosure();

  const formValues = getValues();

  /**
   * if the paymentLimitWeek goes above the total limit of the budget card
   * we adjust it automatically
   */
  useEffect(() => {
    const { unsubscribe } = watch(
      ({ limitPaymentAll, limitPaymentWeek }, { name, type }) => {
        if (
          type === 'change' &&
          (name === 'limitPaymentWeek' || name === 'limitPaymentAll')
        ) {
          if (limitPaymentAll === undefined || limitPaymentWeek === undefined) {
            return;
          }

          if (name === 'limitPaymentAll') {
            setValue('limitPaymentWeek', limitPaymentAll, {
              shouldValidate: true,
            });
          } else if (name === 'limitPaymentWeek') {
            if (limitPaymentWeek > limitPaymentAll) {
              setValue('limitPaymentWeek', limitPaymentAll, {
                shouldValidate: true,
              });
            }
          }
        }
      },
    );
    return () => unsubscribe();
  }, [setValue, watch]);

  /**
   * disable the approve button if
   * - the form is not valid
   * - the user has not selected a deactivation date and the panel is open
   */
  const isApproveButtonDisabled = withDeactivationDate
    ? !formValues.deactivateAt || formValues.deactivateAt === ''
    : !isValid;

  const onFormSubmit = handleSubmit(handleApproveRequest);

  const handleDeactivationAtChange = (val: string) => {
    setValue('deactivateAt', val, { shouldValidate: true });
  };

  return (
    <Box as="form" display="contents" onSubmit={onFormSubmit}>
      <DetailSidebar.Body paddingBottom="space-16" paddingX="space-16">
        <Summary request={request} />
        <VStack alignItems="stretch" gap="space-24">
          {isVirtualBudgetCard ? (
            <VStack gap="space-16">
              <Form.Field
                error={errors.limitPaymentAll?.message}
                label={locales.limitPaymentAll.label}
                width="100%"
              >
                <Form.AmountInput
                  {...register('limitPaymentAll', { valueAsNumber: true })}
                />
              </Form.Field>
              <Callout hideIcon variant="info">
                <Callout.Content>
                  {locales.limitPaymentAll.callout}
                </Callout.Content>
              </Callout>
            </VStack>
          ) : null}
          <Form.Field label={locales.settingLimits}>
            <SunshineCard>
              <SunshineCard.Content spacing="space-8">
                <SunshineCard.Title>
                  <SunshineIcon marginRight="space-8" name="card-recto" />
                  {locales.limitPaymentWeek.label}
                </SunshineCard.Title>

                <Form.Field
                  error={errors.limitPaymentWeek?.message}
                  hint={
                    locales.formatString(locales.limitPaymentWeek.hint, {
                      max: formatAmount({
                        currency: 'EUR',
                        forcePlusSign: false,
                        value: maxLimitPaymentWeek,
                        withNoMinorUnitDigits: false,
                      }),
                    }) as string
                  }
                  width="100%"
                >
                  <Form.Input
                    type="number"
                    {...register('limitPaymentWeek', { valueAsNumber: true })}
                  />
                </Form.Field>
              </SunshineCard.Content>
            </SunshineCard>
          </Form.Field>

          <Form.Field label={locales.deactivationDate}>
            <SunshineCard
              id="with-deactivation-date"
              isChecked={withDeactivationDate}
              onChange={() => {
                /**
                 * reset form state value when toggling the deactivation date switch
                 * we can't use `undefined` because it won't trigger any update
                 */
                setValue('deactivateAt', '');
                resetField('deactivateAt');
                toggleDeactivationDate();
              }}
              variant="switch"
            >
              <SunshineCard.Content>
                <SunshineCard.Title as="label" htmlFor="with-deactivation-date">
                  <SunshineIcon marginRight="space-8" name="calendar" />
                  {locales.deactivateAt.title}
                </SunshineCard.Title>
              </SunshineCard.Content>

              <Collapse in={withDeactivationDate}>
                <Controller
                  control={control}
                  name="deactivateAt"
                  render={({ field: { name, value } }) => (
                    <Form.Radio.Group
                      flex={1}
                      name={name}
                      onChange={handleDeactivationAtChange}
                      paddingTop="space-16"
                      value={value}
                    >
                      {DEACTIVATED_DATE_OPTIONS.map((option) => (
                        <Form.Radio key={option.value} value={option.value}>
                          <Form.Radio.Title variant="primary">
                            {option.title}
                          </Form.Radio.Title>

                          <Form.Radio.Label size="sm">
                            {option.label}
                          </Form.Radio.Label>
                        </Form.Radio>
                      ))}

                      <HStack>
                        <Form.Radio
                          /**
                           * avoid having this option selected without any date selected
                           */
                          isReadOnly
                          value={
                            value && value !== '' && isCustomDate(value)
                              ? value
                              : CUSTOM_DEACTIVATION_DATE_INPUT_VALUE
                          }
                        >
                          <Form.Radio.Title variant="primary">
                            {locales.deactivateAt.options.custom}
                          </Form.Radio.Title>
                        </Form.Radio>

                        <DatePicker
                          customInput={<DateInput />}
                          id={name}
                          onValueChange={handleDeactivationAtChange}
                          placeholderText={locales.deactivateAt.placeholder}
                          value={isCustomDate(value) ? value ?? '' : ''}
                        />
                      </HStack>
                    </Form.Radio.Group>
                  )}
                />
              </Collapse>
            </SunshineCard>
          </Form.Field>

          <Form.Field label={locales.cardOptions}>
            <SunshineCard.Group>
              <Controller
                control={control}
                name="isOptionNfcEnabled"
                render={({ field: { name, onChange, value } }) => (
                  <SunshineCard
                    alignItems="center"
                    id={name}
                    isChecked={value}
                    onChange={onChange}
                    title={locales.isOptionNfcEnabled.label}
                    variant="switch"
                  />
                )}
              />

              <Controller
                control={control}
                name="isOptionOnlineEnabled"
                render={({ field: { name, onChange, value } }) => (
                  <SunshineCard
                    alignItems="center"
                    id={name}
                    isChecked={value}
                    onChange={onChange}
                    title={locales.isOptionOnlineEnabled.label}
                    variant="switch"
                  />
                )}
              />
            </SunshineCard.Group>
          </Form.Field>
        </VStack>
      </DetailSidebar.Body>
      <DetailSidebar.Footer gap="space-0" padding="space-16">
        <PaymentCardRequestFormFooter
          isApprovalDisabled={isApproveButtonDisabled || isRejectingRequest}
          isApprovalLoading={isApprovingRequest}
          isRejectionDisabled={isApprovingRequest}
          isRejectionLoading={isRejectingRequest}
          onReject={handleRejectRequest}
          type={TeamMembersRequestType.VirtualPaymentCardOrder}
        />
      </DetailSidebar.Footer>
    </Box>
  );
};

export default VirtualPaymentCardRequestForm;
